import {makeAutoObservable} from "mobx";
import {ViewController} from "data/types/structure";
import {injectable, inject} from "inversify";
import {Bindings} from "data/constants/bindings";
import type {ILocalizationStore} from "data/stores/localization/localization.store";
import type {IContestsStore, IContest} from "data/stores/contests/contestsStore";
import {first, last, get} from "lodash";
import {ContestStatus, ModalType} from "data/enums";
import type {IModalsStore} from "data/stores/modals/modals.store";
import {DateTime} from "luxon";
import {GAME_TIMEZONE} from "data/constants";

export interface ICalendarPickerController extends ViewController {
	i18n: ILocalizationStore;

	get isOpen(): boolean;

	get selectedDate(): DateTime;

	get dayContests(): IContest[];

	get firstContestDate(): DateTime;

	get lastContestDate(): DateTime;

	get selectedContestDate(): DateTime;

	get isCurrentContestDay(): boolean;

	setSelectedDate: (date: DateTime) => void;
	getDayStatus: (date: DateTime) => ContestStatus;
	handleClose: () => void;
	toCurrentContestDay: () => void;
}

@injectable()
export class CalendarPickerController implements ICalendarPickerController {
	private _dateFormat = "yyyy-LL-dd";
	private _selectedDate: DateTime | null = null;

	constructor(
		@inject(Bindings.ModalsStore) private readonly _modalStore: IModalsStore,
		@inject(Bindings.ContestsStore) private readonly _contestsStore: IContestsStore,
		@inject(Bindings.LocalizationStore) public readonly i18n: ILocalizationStore
	) {
		makeAutoObservable(this);
	}

	get isOpen(): boolean {
		return this._modalStore.modal === ModalType.DAY_PICKER;
	}

	get lockByContests(): number[] {
		return get(this._modalStore.modalContent?.meta, "lockByContests", []) as number[];
	}

	get selectedDate() {
		return this._selectedDate || this.selectedContestDate;
	}

	get dayContests() {
		if (!this.lockByContests.length) {
			return this._contestsStore.list;
		}
		return (
			this.lockByContests?.map((playedContestId) => {
				return this._contestsStore.list.find((contest) => contest.id === playedContestId)!;
			}) || []
		);
	}

	get selectedContestDate(): DateTime {
		return this._toDateTime(this._contestsStore.currentContest?.date ?? "");
	}

	get firstContestDate(): DateTime {
		return this._toDateTime(first(this._contestsStore.list)?.date ?? "");
	}

	get lastContestDate(): DateTime {
		return this._toDateTime(last(this._contestsStore.list)?.date ?? "");
	}

	get isCurrentContestDay(): boolean {
		const contest = this.currentContest;
		if (!this.selectedDate || !contest) {
			return false;
		}
		return this.getContestByDay(this.selectedDate)?.id === contest.id;
	}

	setSelectedDate = (date: DateTime) => {
		const contest = this.getContestByDay(date);
		if (contest) {
			this._selectedDate = date.setZone(GAME_TIMEZONE);
			this.toContestId(contest.id);
			this.handleClose();
		}
	};

	getDayStatus = (date: DateTime) => {
		const contest = this.getContestByDay(date);
		return contest?.status || ContestStatus.Open;
	};

	handleClose = () => {
		this._modalStore.hideModal();
	};

	toCurrentContestDay = () => {
		const lastLockContest = last(this.lockByContests);

		const contest = lastLockContest
			? this._contestsStore.getContestById(lastLockContest)
			: this._contestsStore.currentGameContest;

		if (contest) {
			this.setSelectedDate(this._toDateTime(contest.date));
			this.toContestId(contest.id);
		}
	};

	private get currentContest() {
		const meta = this._modalStore.modalContent?.meta;
		if (meta?.type === "picker" && meta.contestId) {
			return this._contestsStore.getContestById(meta.contestId);
		}
		return this._contestsStore.currentGameContest;
	}

	private toContestId(contestId: number) {
		const meta = this._modalStore.modalContent?.meta;
		if (meta?.type === "picker" && meta.onSelect) {
			meta.onSelect(contestId);
			return;
		}
		this._contestsStore.toContestId(contestId);
	}

	private getContestByDay = (date: DateTime) => {
		const formattedDate = date.toFormat(this._dateFormat);
		return this.dayContests.find((it) => {
			const contestDate = this._toDateTime(it?.date).toFormat(this._dateFormat);
			return contestDate === formattedDate;
		});
	};

	private _toDateTime(date: string) {
		return DateTime.fromISO(date).setZone(GAME_TIMEZONE);
	}
}
